import React from "react";
import { Sparkles, Check } from "lucide-react";
import kobi from "../../assets/images/KUUL TRANS ROUND.png";
import poweredByMtn from "../../assets/images/poweredbymtnwhite.png";
import { KOBIKUUL_BLUE, MTN_YELLOW } from "../../constants/Colors";



interface PromoCardShellProps {
  /** Masthead — avatar and name, or a motif. */
  header: React.ReactNode;
  titleId: string;
  title: string;
  body: string;
  bullets: string[];
  /** Small print above the CTA. */
  note?: string;
  ctaLabel: string;
  onCta: () => void;
  onDismiss: () => void;
  /** Hidden from assistive tech and from taps while this card sits at the
   *  back of the deck. */
  interactive: boolean;
}

/**
 * The chrome both promo cards share: scroll-safe body, yellow CTA, MTN
 * lockup. Lives here so the two cards cannot drift apart visually.
 */
function PromoCardShell({
  header,
  titleId,
  title,
  body,
  bullets,
  note,
  ctaLabel,
  onCta,
  onDismiss,
  interactive,
}: PromoCardShellProps) {
  return (
    <>
      {/* Content scrolls; the actions below stay put. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-3 px-5 pb-1 pt-6">
          {header}

          <p id={titleId} className="m-0 text-lg font-semibold leading-snug">
            {title}
          </p>
          <p className="m-0 text-xs leading-relaxed text-white/70">{body}</p>

          <ul className="m-0 list-none space-y-1.5 p-0 pt-0.5">
            {bullets.map((bullet) => (
              <li
                key={bullet}
                className="flex items-center gap-2 text-xs text-white/90"
              >
                <Check
                  className="h-3.5 w-3.5 shrink-0"
                  strokeWidth={3}
                  style={{ color: MTN_YELLOW }}
                />
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="shrink-0 space-y-2 px-5 pb-5 pt-4">
        {note && (
          <p className="m-0 pb-1 text-center text-[11px] text-white/50">
            {note}
          </p>
        )}
        <button
          type="button"
          onClick={onCta}
          tabIndex={interactive ? 0 : -1}
          className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:brightness-95"
          style={{ backgroundColor: MTN_YELLOW }}
        >
          {ctaLabel}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          tabIndex={interactive ? 0 : -1}
          className="w-full rounded-lg px-4 py-2 text-xs font-medium text-white/60 transition-colors hover:text-white"
        >
          Maybe later
        </button>
        <img
          src={poweredByMtn}
          alt="Powered by MTN"
          className="mx-auto h-6 w-auto opacity-80"
        />
      </div>
    </>
  );
}

interface CardProps {
  onCta: () => void;
  onDismiss: () => void;
  interactive: boolean;
}

/** Card 1: the AI chat. */
export function ChatPromoCard({ onCta, onDismiss, interactive }: CardProps) {
  return (
    <PromoCardShell
      titleId="kobikuul-promo-title"
      header={
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div
              className="absolute -left-1 -top-1 h-14 w-14 animate-pulse rounded-full"
              style={{ backgroundColor: `${KOBIKUUL_BLUE}40` }}
              aria-hidden
            />
            <div className="relative h-12 w-12 overflow-hidden rounded-full bg-white shadow">
              <img src={kobi} alt="" className="h-full w-full object-cover" />
            </div>
            <span
              className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-neutral-900 bg-green-500"
              aria-hidden
            />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-bold">Kobikuul AI</span>
              <span
                className="inline-flex items-center gap-1 rounded-xl bg-white/10 px-2 py-0.5 text-[11px] font-semibold"
                style={{ color: KOBIKUUL_BLUE }}
              >
                <Sparkles className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                Online
              </span>
            </div>
            <p className="m-0 text-xs text-white/60">
              Your healthcare companion
            </p>
          </div>
        </div>
      }
      // CEO, 2026-09-04: lead with the things people actually arrive with
      // rather than with a description of the product.
      title="Want to lose weight? Sleep better? Menstrual pain?"
      body="Kobikuul is your healthcare companion — ask anything about your health, in plain language, any time."
      // Deliberately no question count: the guest allowance is DB-driven (the
      // ANONYMOUS AI tier, editable from the CRM), so a number printed in
      // marketing copy would be the one place it could go stale.
      bullets={[
        "Weight loss, diet and exercise",
        "Better sleep and less stress",
        "Menstrual pain and women's health",
      ]}
      note="Free to start — no sign-up needed."
      ctaLabel="Chat Kobikuul now"
      onCta={onCta}
      onDismiss={onDismiss}
      interactive={interactive}
    />
  );
}

/** Card 2: the games hub. Copy stays on "a quick game" and never claims a
 *  cognitive benefit — Lumosity settled with the FTC in 2016 over exactly
 *  that kind of claim, and we are a health brand. */
export function GamesPromoCard({ onCta, onDismiss, interactive }: CardProps) {
  return (
    <PromoCardShell
      titleId="kobikuul-games-promo-title"
      header={
        <div className="space-y-2.5">
          {/* Kente motif in pure CSS — no image to download on a metered
              connection. */}
          <div
            // Stops short of the close button rather than running under it.
            className="mr-9 h-8 rounded-md"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, #FFCC08 0 14px, #111 14px 18px, #1B7A3E 18px 32px, #111 32px 36px, #C8102E 36px 50px, #111 50px 54px)",
            }}
            aria-hidden
          />
          <span
            className="inline-block rounded-md px-2.5 py-0.5 text-[10px] font-bold tracking-wider text-black"
            style={{ backgroundColor: MTN_YELLOW }}
          >
            NEW · GAMES
          </span>
        </div>
      }
      title="Fancy a quick game?"
      body="Kente Sequence: watch the cloth weave itself, then tap the colours back in order. One round takes a minute."
      bullets={[
        "Kente Sequence — remember the weave",
        "Rounds of about a minute",
        "Free, no sign-up needed",
      ]}
      ctaLabel="Play now"
      onCta={onCta}
      onDismiss={onDismiss}
      interactive={interactive}
    />
  );
}
